// The practice log's panel: today's practice and the streak, the last seven
// days, this song's time and whether it's on the player's learning list,
// and that list. Time with the dock's tools counts by itself; the timer is
// for practising without them.

import React, { useEffect } from 'react';
import classNames from 'classnames';
import { KeyHint, spaceLabel } from './shortcuts';

export function formatDuration(seconds) {
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return seconds > 0 ? gettext('under a minute') : interpolate(gettext('%s min'), [0]);
    if (minutes < 60) return interpolate(gettext('%s min'), [minutes]);
    const hours = Math.floor(minutes / 60);
    return interpolate(gettext('%(hours)s h %(minutes)s min'), { hours, minutes: minutes % 60 }, true);
}

const clock = seconds => {
    const pad = n => String(n).padStart(2, '0');
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds / 60) % 60;
    return h ? `${h}:${pad(m)}:${pad(seconds % 60)}` : `${m}:${pad(seconds % 60)}`;
};

const parseDate = iso => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
};

function daysAgo(iso, today) {
    const days = Math.round((parseDate(today) - parseDate(iso)) / 86400000);
    if (days <= 0) return gettext('today');
    if (days === 1) return gettext('yesterday');
    return interpolate(ngettext('%s day ago', '%s days ago', days), [days]);
}

// Translated rather than toLocaleDateString's narrow weekday: Chrome has no
// Icelandic locale data and would show English initials.
const weekday = iso => [
    pgettext('initial of Sunday', 'S'),
    pgettext('initial of Monday', 'M'),
    pgettext('initial of Tuesday', 'T'),
    pgettext('initial of Wednesday', 'W'),
    pgettext('initial of Thursday', 'T'),
    pgettext('initial of Friday', 'F'),
    pgettext('initial of Saturday', 'S'),
][parseDate(iso).getDay()];

export default function PracticeLog({ log, isAuthenticated, songId, onLogin }) {
    useEffect(() => {
        log.refresh();
    }, [songId, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    const timer = (
        <div className="practice-row practice-log-timer">
            <button
                type="button"
                className={classNames('practice-play', { playing: log.timerOn })}
                onClick={log.timerOn ? log.stopTimer : log.startTimer}
                aria-label={log.timerOn ? gettext('Pause the practice timer') : gettext('Start the practice timer')}
            >
                {log.timerOn ? <span className="icon-pause" /> : <span className="icon-play" />}
                <KeyHint k={spaceLabel()} />
            </button>
            <div>
                <strong className="practice-log-clock">{clock(log.sessionSeconds)}</strong>
                <span className="practice-hint">
                    {log.counting && !log.timerOn ? gettext('Counting while you play along.') : gettext('Practice on this song')}
                </span>
            </div>
        </div>
    );

    if (!isAuthenticated) {
        return (
            <div className="practice-log practice-tool">
                {timer}
                <p className="practice-hint">{gettext('Log in to keep a practice log: time on each song, the songs you are learning, and your streak.')}</p>
                <button type="button" className="practice-pill practice-pill--strong" onClick={onLogin}>{gettext('Log in')}</button>
            </div>
        );
    }

    const { summary } = log;
    if (!summary) {
        return (
            <div className="practice-log practice-tool">
                {timer}
                <p className="practice-hint">{log.failed ? gettext("Couldn't load your practice log.") : gettext('Loading…')}</p>
            </div>
        );
    }

    const unsent = log.unsent();
    const today = summary.today + unsent;
    const week = summary.week.map((day, i) => (i === summary.week.length - 1 ? { ...day, seconds: day.seconds + unsent } : day));
    const most = Math.max(15 * 60, ...week.map(day => day.seconds));
    const song = summary.song || { seconds: 0, status: null };
    const songSeconds = song.seconds + unsent;
    const others = (summary.learning || []).filter(item => item.id !== songId);

    return (
        <div className="practice-log practice-tool">
            <div className="practice-log-stats">
                <div>
                    <span className="practice-log-number">{formatDuration(today)}</span>
                    <span className="practice-log-label">{gettext('today')}</span>
                </div>
                <div>
                    <span className="practice-log-number">{summary.streak}</span>
                    <span className="practice-log-label">{ngettext('day in a row', 'days in a row', summary.streak)}</span>
                </div>
                <div className="practice-log-week" aria-label={gettext('The last seven days')}>
                    {week.map(day => (
                        <span key={day.date} className="practice-log-day" title={`${day.date}: ${formatDuration(day.seconds)}`}>
                            <span className="bar"><span style={{ height: `${Math.round(100 * day.seconds / most)}%` }} /></span>
                            <span className="name">{weekday(day.date)}</span>
                        </span>
                    ))}
                </div>
            </div>

            {timer}

            <div className="practice-section">
                <p className="practice-log-song">
                    {songSeconds > 0 ?
                        // Translators: %s is a duration such as "5 min". If its abbreviations end in a full stop, leave off the sentence's own.
                        interpolate(gettext("You've practised this song for %s."), [formatDuration(songSeconds)]) :
                        gettext("You haven't practised this song yet.")}
                </p>
                <div className="practice-row">
                    {song.status === 'learned' ?
                        <>
                            <span className="practice-log-learned">✓ {gettext("You've learnt it")}</span>
                            <button type="button" className="practice-link" onClick={() => log.changeLearning('learning')}>{gettext('Still learning it')}</button>
                        </>
                        : song.status === 'learning' ?
                            <>
                                <button type="button" className="practice-pill practice-pill--strong" onClick={() => log.changeLearning('learned')}>✓ {gettext("I've learnt it")}</button>
                                <button type="button" className="practice-link" onClick={() => log.changeLearning(null)}>{gettext('Take it off my list')}</button>
                            </>
                            :
                            <button type="button" className="practice-pill" onClick={() => log.changeLearning('learning')}>+ {gettext("I'm learning this song")}</button>
                    }
                </div>
            </div>

            {others.length > 0 &&
                <div className="practice-section">
                    <h3 className="practice-log-heading">{gettext("Songs you're learning")}</h3>
                    <ul className="practice-log-list">
                        {others.map(item => (
                            <li key={item.id}>
                                <a href={item.url}>{item.title}</a>
                                <span>{item.seconds ? formatDuration(item.seconds) : ''}{item.last ? ` · ${daysAgo(item.last, summary.date)}` : ''}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            }
        </div>
    );
}
